import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import TeAraroaMap from '../Map/TeAraroaMap';
import { observationApi } from '../../services/api';
import {
  OBSERVATION_TYPE_COLORS,
  OBSERVATION_TYPE_LABELS,
  TrailObservationDto,
  TrailObservationStatsDto,
} from '../../types/Observation';

const PAGE_SIZE = 25;

// The map shows every matching observation, not just the current list page.
// Far beyond a season's expected volume.
const MAP_FETCH_LIMIT = 1000;

const LEGEND: { color: string; label: string }[] = [
  { color: '#16a34a', label: 'Vegetation' },
  { color: '#92400e', label: 'Track surface' },
  { color: '#d97706', label: 'Marking / signage' },
  { color: '#dc2626', label: 'Hazards & wasps' },
  { color: '#2563eb', label: 'Maps / notes' },
  { color: '#0d9488', label: 'Camping' },
  { color: '#6b7280', label: 'Other' },
];

export default function ObservationsPage() {
  const [observations, setObservations] = useState<TrailObservationDto[]>([]);
  const [stats, setStats] = useState<TrailObservationStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Type is filtered client-side within the page (it lives inside the
  // observation's JSON payload); dates and deleted are server-side.
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const loadObservations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await observationApi.getObservations({
        page,
        pageSize: PAGE_SIZE,
        includeDeleted,
        from: dateFrom || undefined,
        to: dateTo || undefined,
      });
      setObservations(response.reports);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (e) {
      console.error('Failed to load observations', e);
      setError('Failed to load observations.');
    } finally {
      setLoading(false);
    }
  }, [page, includeDeleted, dateFrom, dateTo]);

  const loadStats = useCallback(async () => {
    try {
      setStats(await observationApi.getStats());
    } catch (e) {
      console.error('Failed to load observation stats', e);
    }
  }, []);

  const [mapObservations, setMapObservations] = useState<TrailObservationDto[]>([]);
  const loadMapObservations = useCallback(async () => {
    try {
      const response = await observationApi.getObservations({
        page: 1,
        pageSize: MAP_FETCH_LIMIT,
        includeDeleted: false,
        from: dateFrom || undefined,
        to: dateTo || undefined,
      });
      setMapObservations(response.reports);
    } catch (e) {
      console.error('Failed to load observations for the map', e);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadObservations();
  }, [loadObservations]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadMapObservations();
  }, [loadMapObservations]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this observation? It stays in the database.')) return;
    await observationApi.deleteObservation(id);
    await Promise.all([loadObservations(), loadStats(), loadMapObservations()]);
  };

  const handleRestore = async (id: string) => {
    await observationApi.restoreObservation(id);
    await Promise.all([loadObservations(), loadStats(), loadMapObservations()]);
  };

  const clearFilters = () => {
    setIncludeDeleted(false);
    setDateFrom('');
    setDateTo('');
    setTypeFilter('');
    setPage(1);
  };

  // Markers: original observations only (withdrawal rows reference an
  // original and carry no position of interest), honouring the type filter.
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const fittedRef = useRef(false);
  const markerObservations = mapObservations.filter(
    (o) =>
      !o.followUpOf &&
      o.latitude != null &&
      o.longitude != null &&
      (!typeFilter || o.type === typeFilter),
  );

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const bounds = new maplibregl.LngLatBounds();
    markerObservations.forEach((observation) => {
      // Popup built via textContent — the note is hiker-typed text.
      const popup = document.createElement('div');
      popup.className = 'text-sm';
      const title = document.createElement('div');
      title.className = 'font-semibold';
      title.textContent =
        OBSERVATION_TYPE_LABELS[observation.type ?? ''] ?? 'Observation';
      popup.appendChild(title);
      const when = document.createElement('div');
      when.className = 'text-gray-500';
      when.textContent = `Observed ${new Date(observation.observedAt).toLocaleDateString()}`;
      popup.appendChild(when);
      if (observation.note) {
        const note = document.createElement('div');
        note.className = 'mt-1';
        note.textContent = observation.note;
        popup.appendChild(note);
      }

      const marker = new maplibregl.Marker({
        color: OBSERVATION_TYPE_COLORS[observation.type ?? ''] ?? '#6b7280',
      })
        .setLngLat([observation.longitude!, observation.latitude!])
        .setPopup(new maplibregl.Popup({ offset: 24 }).setDOMContent(popup))
        .addTo(map);
      markersRef.current.push(marker);
      bounds.extend([observation.longitude!, observation.latitude!]);
    });

    // Frame the observations once on first data; later filter changes keep
    // the admin's current view.
    if (!bounds.isEmpty() && !fittedRef.current) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 12 });
      fittedRef.current = true;
    }
  }, [markerObservations]);

  useEffect(() => {
    syncMarkers();
  }, [syncMarkers]);

  const handleMapLoad = useCallback(
    (map: MapLibreMap) => {
      mapRef.current = map;
      syncMarkers();
    },
    [syncMarkers],
  );

  const visibleObservations = typeFilter
    ? observations.filter((o) => o.type === typeFilter)
    : observations;

  if (loading && observations.length === 0) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/" className="text-indigo-600 hover:text-indigo-900">
            ← Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Trail Observations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Hiker reports of track conditions, hazards, and opportunities. Not visible to other hikers.
          </p>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-gray-900">{stats.totalObservations}</div>
              <div className="text-sm text-gray-500">Total Observations</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{stats.observationsLast7Days}</div>
              <div className="text-sm text-gray-500">Last 7 Days</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-emerald-600">{stats.independentReporters}</div>
              <div className="text-sm text-gray-500">Independent Reporters</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="text-2xl font-bold text-amber-600">
                {Object.keys(stats.typeCounts).length}
              </div>
              <div className="text-sm text-gray-500">Types Reported</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700">
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All types</option>
                {Object.entries(OBSERVATION_TYPE_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                    {stats?.typeCounts[id] ? ` (${stats.typeCounts[id]})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Observed from</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Observed to</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="inline-flex items-center text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(1); }}
                  className="mr-2"
                />
                Include removed
              </label>
            </div>
          </div>
        </div>

        {/* Map of all matching observations */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Observation Map</h3>
            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
              {LEGEND.map((entry) => (
                <span key={entry.label} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  {entry.label}
                </span>
              ))}
              <span>{markerObservations.length} shown</span>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg">
            <TeAraroaMap
              center={[172.6, -43.5]}
              zoom={5}
              height="480px"
              onMapLoad={handleMapLoad}
              showControls={true}
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Observation list */}
        <div className="space-y-4">
          {visibleObservations.length === 0 && !loading && (
            <div className="bg-white p-10 rounded-lg shadow text-center text-gray-500">
              No observations match the current filters.
            </div>
          )}
          {visibleObservations.map((observation) => (
            <div
              key={observation.id}
              className={`bg-white p-6 rounded-lg shadow ${observation.isDeleted ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className="px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
                    style={{
                      backgroundColor:
                        OBSERVATION_TYPE_COLORS[observation.type ?? ''] ?? '#6b7280',
                    }}
                  >
                    {OBSERVATION_TYPE_LABELS[observation.type ?? ''] ??
                      (observation.followUpOf ? 'Withdrawal' : 'Observation')}
                  </span>
                  {observation.followUpOf && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      Withdrawn by reporter
                    </span>
                  )}
                  {observation.isDeleted && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-600">
                      Removed
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    Observed {new Date(observation.observedAt).toLocaleString()}
                  </span>
                </div>
                <div className="flex gap-2">
                  {!observation.isDeleted ? (
                    <button
                      onClick={() => handleDelete(observation.id)}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRestore(observation.id)}
                      className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                    >
                      Restore
                    </button>
                  )}
                </div>
              </div>

              {observation.note && (
                <p className="mt-3 text-gray-800 bg-gray-50 rounded-lg px-4 py-2">{observation.note}</p>
              )}

              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-500">
                <div>
                  {observation.latitude != null && observation.longitude != null ? (
                    <a
                      href={`https://www.google.com/maps?q=${observation.latitude},${observation.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      📍 {observation.latitude.toFixed(5)}, {observation.longitude.toFixed(5)}
                    </a>
                  ) : (
                    <span>No position</span>
                  )}
                  {observation.positionAccuracy != null && (
                    <span className="ml-1">(±{Math.round(observation.positionAccuracy)} m)</span>
                  )}
                </div>
                <div title={observation.userId}>
                  Reporter: {observation.userName || observation.userId.slice(0, 12) + '…'}
                </div>
                <div title={observation.followUpOf ?? undefined}>
                  {observation.followUpOf
                    ? `Withdraws ${observation.followUpOf.slice(0, 8)}…`
                    : `Observation ${observation.id.slice(0, 8)}…`}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {totalCount} row{totalCount === 1 ? '' : 's'} · page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
